import React, { useState, useEffect } from 'react';
import {
    Button,
    TextField,
    CircularProgress,
    Typography,
    InputAdornment,
    Icon
} from '@material-ui/core';
import { useDispatch } from 'react-redux';
import VerificationCodeDialog from './VerificationCodeDialog';
import { loginTraining, verifyTrainingCode } from 'app/store/actions/training.actions';
import { submitLoginForTrainingSession } from 'app/auth/store/actions/auth.actions';

const TrainingSessionLogin = () => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [formState, setFormState] = useState({
        fullName: '',
        email: '',
        sessionCode: ''
    });
    const [verificationOpen, setVerificationOpen] = useState(false);
    const [verificationLoading, setVerificationLoading] = useState(false);
    const [verificationError, setVerificationError] = useState('');
    const [domainError, setDomainError] = useState('');
    const [allowedDomains, setAllowedDomains] = useState([]);
    const [licenseError, setLicenseError] = useState('');

    // Clean up states on component mount to prevent stale state issues
    useEffect(() => {
        setError('');
        setDomainError('');
        setLicenseError('');
        setAllowedDomains([]);
        setVerificationError('');
        setVerificationOpen(false);
    }, []);

    const handleChange = (e) => {
        // Clear all error states when user starts typing
        setError('');
        setDomainError('');
        setLicenseError('');
        setAllowedDomains([]);
        
        setFormState({
            ...formState,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setDomainError('');
        setAllowedDomains([]);
        setLicenseError('');

        try {
            const result = await dispatch(loginTraining(formState));
            if (result && result.verificationRequired) {
                setLoading(false);
                setVerificationOpen(true);
            } else {
                await dispatch(submitLoginForTrainingSession(formState.fullName, formState.sessionCode, formState.email));
            }
        } catch (err) {
            // Enhanced error handling
            console.error("Login error:", err);
            
            // Ensure err is defined and has expected structure
            if (!err) {
                setError('An unexpected error occurred');
                setLoading(false);
                return;
            }
            
            if (err.licenseError) {
                // License capacity error
                setLicenseError(err.message || 'License capacity reached. Cannot create new session.');
            } else if (err.allowedDomains && Array.isArray(err.allowedDomains)) {
                // Domain restriction error
                setDomainError(err.message || 'Email domain not allowed');
                setAllowedDomains(err.allowedDomains);
            } else if (err.responseData && err.responseData.error) {
                // Server returned explicit error message
                setError(err.responseData.error);
            } else if (err.response && err.response.data) {
                // Standard axios error with response data
                const responseData = err.response.data;
                if (responseData.reason === 'LicenseCapacityReached') {
                    setLicenseError(responseData.message || 'License capacity reached. Cannot create new session.');
                } else if (responseData.message && responseData.message.includes('capacity')) {
                    setLicenseError(responseData.message);
                } else {
                    setError(responseData.message || responseData.error || 'Server error occurred');
                }
            } else {
                // Generic error fallback
                setError((err && err.message) || 'An unexpected error occurred');
            }
            
            setLoading(false);
        }
    };

    const handleVerifyCode = async (code) => {
        setVerificationLoading(true);
        setVerificationError('');
        try {
            const result = await dispatch(verifyTrainingCode({ ...formState, code }));
            if (result.success) {
                await dispatch(submitLoginForTrainingSession(
                    formState.fullName,
                    formState.sessionCode,
                    formState.email
                ));
            } else {
                setVerificationError(result.error);
            }
        } catch (err) {
            setVerificationError('An unexpected error occurred');
        } finally {
            setVerificationLoading(false);
        }
    };

    // New handler to re-send verification code
    const handleResendCode = async () => {
        setVerificationError('');
        setLoading(true);
        try {
            await dispatch(loginTraining(formState));
            setLoading(false);
        } catch (err) {
            // Apply same defensive error handling as main submit
            if (!err) {
                setError('An unexpected error occurred');
                setLoading(false);
                return;
            }
            setError((err && err.message) || 'Failed to resend code.');
            setLoading(false);
        }
    };

    return (
        <div className="w-full">
            <Typography variant="h6" className="text-center md:w-full mb-48">
                LOGIN TO WORKSHOP SESSION
            </Typography>
            <form className="flex flex-col justify-center w-full" onSubmit={handleSubmit}>
                <TextField
                    className="mb-16"
                    label="Full Name"
                    name="fullName"
                    value={formState.fullName}
                    onChange={handleChange}
                    variant="outlined"
                    required
                    fullWidth
                    disabled={loading}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <Icon className="text-20" color="action">
                                    person
                                </Icon>
                            </InputAdornment>
                        )
                    }}
                />
                <TextField
                    className="mb-16"
                    label="Email"
                    name="email"
                    type="email"
                    value={formState.email}
                    onChange={handleChange}
                    variant="outlined"
                    required
                    fullWidth
                    disabled={loading}
                    error={!!domainError}
                    helperText={domainError ? `Email domain not allowed. Please use an email from one of these domains: ${allowedDomains.join(', ')}` : ''}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <Icon className="text-20" color="action">
                                    email
                                </Icon>
                            </InputAdornment>
                        )
                    }}
                />
                <TextField
                    className="mb-16"
                    label="Workshop Session Code"
                    name="sessionCode"
                    value={formState.sessionCode}
                    onChange={handleChange}
                    variant="outlined"
                    required
                    fullWidth
                    disabled={loading}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <Icon className="text-20" color="action">
                                    school
                                </Icon>
                            </InputAdornment>
                        )
                    }}
                />
                {error && (
                    <div className="text-red-500 mt-8 text-center">{error}</div>
                )}
                {licenseError && (
                    <div className="text-red-500 mt-8 text-center">{licenseError}</div>
                )}
                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    className="w-full mx-auto mt-16"
                    disabled={!formState.fullName || !formState.email || !formState.sessionCode || loading}
                    style={{ minHeight: 48, minWidth: 280 }}
                >
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', minWidth: 240 }}>
                        {loading ? <CircularProgress size={24} /> : 'Login to Workshop Session'}
                    </div>
                </Button>
            </form>

            <VerificationCodeDialog
                open={verificationOpen}
                onClose={() => {
                    if (!verificationLoading) {
                        setVerificationOpen(false);
                        setVerificationError('');
                    }
                }}
                onVerify={handleVerifyCode}
                onResend={handleResendCode}
                email={formState.email}
                loading={verificationLoading}
                error={verificationError}
            />
        </div>
    );
};

export default TrainingSessionLogin;
